import { useNavigate, useLocation } from 'react-router-dom'
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js'
import type { LeafletMouseEvent, LatLngLiteral } from 'leaflet'
import { useCallback } from 'react'
import useParam, { type UrlParam } from '../../../shared/hooks/useParam'
import { zoomParam, locationParam, legendOpenParam, polygonParam } from '../query-params'
import useBuildQueryString from '../../../shared/hooks/useBuildQueryString'
import fetchNearestDetail from '../legacy/services/nearest-detail/nearest-detail'
import { isAuthenticated } from '../../../shared/utils/auth/auth'
import { toDataDetail, toGeoSearch } from '../../../links'

import useLayerContext from '../../../shared/contexts/Layers/useLayerContext'
import { useMapContext } from '../../../shared/contexts/map/MapContext'

const useMapClickHandler = (enableLocation = true) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { drawToolLocked } = useMapContext()
  const { legendLeafletLayers } = useLayerContext()
  const [zoom] = useParam(zoomParam)
  const { buildQueryString } = useBuildQueryString()

  const appInsights = useAppInsightsContext()
  const trackEvent = useTrackEvent(appInsights, 'OPEN_LAYER_DETAIL', {})

  async function handleMapClick(e: LeafletMouseEvent) {
    const layers = legendLeafletLayers
      .filter(({ layer }) => layer.detailUrl && zoom >= layer.minZoom)
      .map(({ layer }) => layer)

    let nearestDetails = null
    if (layers.length > 0) {
      nearestDetails = await fetchNearestDetail(
        { latitude: e.latlng.lat, longitude: e.latlng.lng },
        layers,
        zoom,
      )
    }

    if (nearestDetails) {
      const { type, subType, id } = nearestDetails
      if (isAuthenticated()) {
        trackEvent({ category: 'map-layer', type, subType })
      }
      const { pathname } = toDataDetail({ type, subtype: subType ?? ('' as any), id })
      const queryString = buildQueryString([[locationParam, e.latlng]], [legendOpenParam])

      navigate(`${pathname ?? ''}?${queryString}`)
    } else if (enableLocation) {
      const paramsToAdd: [UrlParam<LatLngLiteral | null>, LatLngLiteral][] = [
        [locationParam, e.latlng],
      ]
      const paramsToRemove = [polygonParam, legendOpenParam]
      const queryString = buildQueryString(paramsToAdd, paramsToRemove)
      const { pathname } = toGeoSearch()

      navigate(`${pathname ?? ''}?${queryString}`)
    }
  }

  return useCallback(
    (event: LeafletMouseEvent) => {
      if (!drawToolLocked) {
        handleMapClick(event).catch(console.error)
      }
    },
    [location, enableLocation, legendLeafletLayers, drawToolLocked],
  )
}

export default useMapClickHandler
